import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarClock, NotebookPen, X } from "lucide-react";
import DailyOrganiser from "./DailyOrganiser";
import PonderrNotebook from "./PonderrNotebook";

export default function DrawerHost() {
  const [open, setOpen] = useState(null); // "left" | "right" | null

  // Swipe gestures from the screen edges (touch devices).
  useEffect(() => {
    let sx = 0, sy = 0, fromLeft = false, fromRight = false;
    const onStart = (e) => {
      const t = e.touches[0]; sx = t.clientX; sy = t.clientY;
      fromLeft = sx < 28; fromRight = sx > window.innerWidth - 28;
    };
    const onEnd = (e) => {
      const t = e.changedTouches[0];
      const dx = t.clientX - sx, dy = Math.abs(t.clientY - sy);
      if (dy > 80) return;
      if (fromLeft && dx > 60) setOpen("left");
      else if (fromRight && dx < -60) setOpen("right");
      else if (open && Math.abs(dx) > 80) setOpen(null);
    };
    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchend", onEnd, { passive: true });
    return () => { document.removeEventListener("touchstart", onStart); document.removeEventListener("touchend", onEnd); };
  }, [open]);

  return (
    <>
      {/* Edge handles */}
      {!open && (
        <>
          <button onClick={() => setOpen("left")} data-testid="drawer-handle-left"
            className="fixed left-0 top-1/2 -translate-y-1/2 z-40 bg-[#FDE047] border-2 border-black border-l-0 rounded-r-xl py-4 px-1.5 shadow-[3px_3px_0_0_#000] hover:px-2.5 transition-all"
            title="Daily Organiser">
            <CalendarClock size={18} />
          </button>
          <button onClick={() => setOpen("right")} data-testid="drawer-handle-right"
            className="fixed right-0 top-1/2 -translate-y-1/2 z-40 bg-[#C4B5FD] border-2 border-black border-r-0 rounded-l-xl py-4 px-1.5 shadow-[-3px_3px_0_0_#000] hover:px-2.5 transition-all"
            title="Ponderr Notebook">
            <NotebookPen size={18} />
          </button>
        </>
      )}

      <AnimatePresence>
        {open && (
          <>
            <motion.div className="fixed inset-0 bg-black/40 z-40" data-testid="drawer-overlay"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setOpen(null)} />
            <motion.aside
              key={open}
              className={`fixed top-0 bottom-0 z-50 bg-white border-black shadow-2xl flex flex-col
                ${open === "left" ? "left-0 border-r-2 w-[92vw] max-w-md" : "right-0 border-l-2 w-[95vw] max-w-3xl"}`}
              initial={{ x: open === "left" ? "-100%" : "100%" }}
              animate={{ x: 0 }} exit={{ x: open === "left" ? "-100%" : "100%" }}
              transition={{ type: "spring", stiffness: 320, damping: 34 }}
              data-testid={`drawer-${open}`}>
              <div className={`flex items-center gap-2 p-3 border-b-2 border-black ${open === "left" ? "bg-[#FDE047]" : "bg-[#C4B5FD]"}`}>
                {open === "left" ? <CalendarClock size={18} /> : <NotebookPen size={18} />}
                <div className="font-black display">{open === "left" ? "Daily Organiser" : "Ponderr Notebook"}</div>
                <button className="ml-auto nb-btn nb-btn-ghost px-2 py-1" onClick={() => setOpen(null)} data-testid="drawer-close"><X size={16} /></button>
              </div>
              <div className={`flex-1 min-h-0 ${open === "left" ? "overflow-y-auto p-4" : "overflow-hidden"}`}>
                {open === "left" ? <DailyOrganiser /> : <PonderrNotebook />}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
